import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CHAT_STRINGS_PT, ChatStrings } from '../../chat.strings';
import { ChatColleague, ChatColleagueScope, ChatColleagueStore } from '../../chat.model';
import { roleLabel } from '../../chat.format';

/**
 * O seletor de colegas (2026-10-28, decisão do utilizador), usado em "Nova
 * conversa", "Convidar" e "Transferir": só consultores ativos e com email
 * confirmado (`GET /chat/colleagues`), por omissão os da loja de quem pede, com
 * "Utilizadores da rede" e o filtro por agência; o MASTER vê sempre a rede.
 * Só apresentação: o contentor carrega a lista e recebe cada mudança.
 */
@Component({
  selector: 'chat-colleague-picker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './chat-colleague-picker.component.html',
  styleUrls: ['./chat-colleague-picker.component.css']
})
export class ChatColleaguePickerComponent implements OnChanges, OnDestroy {
  @Input() master = false;
  @Input() scope: ChatColleagueScope = 'store';
  @Input() stores: ChatColleagueStore[] = [];
  @Input() storeId: number | null = null;
  @Input() users: ChatColleague[] = [];
  @Input() loading = false;
  @Input() selectedId: string | null = null;
  /** `userAppId`s que não aparecem (já na conversa, convite pendente, o responsável). */
  @Input() exclude: string[] = [];
  /** Base das fotos (`/users/files/`). */
  @Input() profileBase = '';
  @Input() strings: ChatStrings = CHAT_STRINGS_PT;

  @Output() scopeChange = new EventEmitter<ChatColleagueScope>();
  @Output() storeIdChange = new EventEmitter<number | null>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() selectedIdChange = new EventEmitter<string | null>();

  visible: ChatColleague[] = [];
  private search$ = new Subject<string>();
  private sub: Subscription;

  constructor() {
    this.sub = this.search$.pipe(debounceTime(250), distinctUntilChanged()).subscribe(v => this.searchChange.emit(v));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['users'] || changes['exclude']) {
      const out = new Set((this.exclude || []).map(String));
      this.visible = (this.users || []).filter(u => !out.has(String(u.userAppId)));
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  onSearch(event: Event): void {
    this.search$.next(((event.target as HTMLInputElement).value || '').trim());
  }

  setScope(scope: ChatColleagueScope): void {
    if (scope !== this.scope) {
      this.scopeChange.emit(scope);
    }
  }

  pick(u: ChatColleague): void {
    this.selectedIdChange.emit(this.selectedId === u.userAppId ? null : u.userAppId);
  }

  photo(u: ChatColleague): string | undefined {
    if (!u.photo) {
      return undefined;
    }
    return /^(https?:)?\//.test(u.photo) ? u.photo : this.profileBase + u.photo;
  }

  subtitle(u: ChatColleague): string {
    const role = roleLabel(u.role || undefined, this.strings);
    const stores = (u.stores || []).map(s => s.name).join(', ');
    return [role, stores].filter(x => !!x).join(' · ');
  }

  trackByUser = (_i: number, u: ChatColleague) => u.userAppId;
}
